package com.essntl.uikit.theme

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.*
import androidx.compose.ui.graphics.Color
import com.essntl.uikit.components.image.setSingletonImageLoaderFactory
import core.config.buildFlavor

private val DarkColorScheme = darkColorScheme()

private val LightColorScheme = lightColorScheme()

private val DarkEsColorScheme = EsColorScheme(
    // Background
    bgPrimary = Primary100,
    bgSecondary = Grayscale100,
    bgSecondaryHover = Grayscale200,
    bgContrast = Primary800,
    bgWarning = Orange500,
    bgSuccess = Green500,
    bgDisable = Grayscale400,
    bgError = Red500,
    bgTable = Color(0xFFF7F7F8),
    bgTableHover = Color(0x1F2845DF),
    bgTableActive = Color(0x332845DF),

    // Borders
    borderPrimary = Grayscale300,
    borderSecondary = Grayscale400,
    borderDisabled = Grayscale200,
    borderFocus = Color(0xFFe5e9fb),
    borderWarning = Orange500,
    borderSuccess = Green500,
    borderError = Red500,
    borderAccent = Primary500,

    // Text
    textPrimary = Grayscale800,
    textSecondary = Grayscale600,
    textTertiary = Grayscale500,
    textDisabled = Grayscale400,
    textContrast = Grayscale100,
    textWarning = Orange500,
    textSuccess = Green500,
    textError = Red500,
    textAccent = Primary500,

    // Icons
    iconPrimary = Primary800,
    iconSecondary = Grayscale500,
    iconDisabled = Grayscale400,
    iconContrast = Grayscale100,
    iconWarning = Orange500,
    iconSuccess = Green500,
    iconError = Red500,
    iconAccent = Primary500,

    // Controls
    primary = Primary800,
    primaryHover = Primary700,
    primaryPressed = Primary900,
    primaryDisabled = Color(0xFFE6E6EB),

    tinted = Primary200,
    tintedHover = Primary100,
    tintedPressed = Primary300,
    tintedDisabled = Primary100,

    secondary = Grayscale200,
    secondaryHover = Grayscale300,
    secondaryPressed = Grayscale400,
    secondaryDisabled = Grayscale200,

    ghost = Grayscale100,
    ghostHover = Grayscale400,
    ghostPressed = Grayscale300,

    destructive = Red100,
    destructiveHover = Red200,
    destructivePressed = Red300,
)

private val LightEsColorScheme = DarkEsColorScheme

internal val LocalEsColorScheme = staticCompositionLocalOf { LightEsColorScheme }

internal val LocalEsTypography = staticCompositionLocalOf { EsTypography() }

object Branding {
    private val essntlBrand = EsColorScheme.Brand(
        primary = Color(0x0E073D),
    )

    private val beBrand = EsColorScheme.Brand(
        primary = Color.Black,
    )

    val backgroundPrimary =
        if (buildFlavor.isDemo)
            essntlBrand.primary else
            beBrand.primary
}

val esColorScheme: EsColorScheme
    @Composable
    @ReadOnlyComposable
    get() = LocalEsColorScheme.current

val MaterialTheme.esColorScheme: EsColorScheme
    @Composable
    @ReadOnlyComposable
    get() = LocalEsColorScheme.current

val esTypography: EsTypography
    @Composable
    @ReadOnlyComposable
    get() = LocalEsTypography.current

val MaterialTheme.esTypography: EsTypography
    @Composable
    @ReadOnlyComposable
    get() = LocalEsTypography.current

@Composable
fun EssntlTheme(
    darkTheme: Boolean = isSystemInDarkTheme(),
    content: @Composable () -> Unit,
) {
    setSingletonImageLoaderFactory()

    val colorScheme = if (darkTheme) {
        DarkColorScheme
    } else {
        LightColorScheme
    }

    val esColorScheme = if (darkTheme) {
        DarkEsColorScheme
    } else {
        LightEsColorScheme
    }

    val esTypography = getTypography()

    CompositionLocalProvider(
        LocalEsColorScheme provides esColorScheme,
        LocalEsTypography provides esTypography,
    ) {
        MaterialTheme(
            colorScheme = colorScheme,
            shapes = Shapes,
            content = content,
        )
    }
}
